import { GEAR_ATTRS, GEAR_BASES, GEM_ATTR, GEM_CUT_BP, UNIQUE_EFFECT_BP, attrValue, type GearAttr } from '@somnhold/content';
import { itemAttrs, itemBudget, itemSockets, type ItemInstance } from '@somnhold/sim';

/** One attribute side by side: what the piece gives and what the worn one gives (bp, as applied). */
export interface AttrDiff { attr: GearAttr; mine: number; worn: number }
/** A power one of the two pieces has and the other doesn't (or has weaker). */
export interface PowerDiff { kind: 'effect' | 'unique' | 'trait'; key: string; gain: boolean }
/** better: nothing lost; worse: nothing gained; mixed: both; same: no difference; empty: nothing worn there. */
export type Verdict = 'better' | 'worse' | 'mixed' | 'same' | 'empty';
export interface GearCompare { attrs: AttrDiff[]; powers: PowerDiff[]; verdict: Verdict }

/** What a piece adds to each attribute, its forge pluses and the gem in its setting included (as gearMods applies them). */
export const appliedAttrs = (item: ItemInstance): Partial<Record<GearAttr, number>> => {
  const out: Partial<Record<GearAttr, number>> = {};
  const a = itemAttrs(item);
  for (const k of GEAR_ATTRS) if (a[k]) out[k] = attrValue(k, a[k] ?? 0);
  for (const g of itemSockets(item)) {
    if (!g) continue;
    const k = GEM_ATTR[g.gem];
    out[k] = (out[k] ?? 0) + attrValue(k, Math.trunc(((GEM_CUT_BP[g.cut] as number) * itemBudget(item)) / 10000));
  }
  return out;
};

const powers = (item: ItemInstance): Map<string, [PowerDiff['kind'], number]> => {
  const m = new Map<string, [PowerDiff['kind'], number]>();
  if (item.effect) m.set(`effect:${item.effect}`, ['effect', item.unique ? UNIQUE_EFFECT_BP : itemBudget(item)]);
  if (item.unique) m.set(`unique:${item.unique}`, ['unique', 1]);
  // A base with a trait (its kind of piece) is a power of its own: a shield's block, a sickle's melee.
  if (item.base && !item.unique && Object.keys(GEAR_BASES[item.base].trait).length) m.set(`trait:${item.base}`, ['trait', 1]);
  return m;
};

/** A bag piece against what the hero wears in its slot: attribute by attribute, its powers, and a one-word verdict. */
export const compareGear = (item: ItemInstance, worn: ItemInstance | null | undefined): GearCompare => {
  const mine = appliedAttrs(item);
  const theirs = worn ? appliedAttrs(worn) : {};
  const attrs: AttrDiff[] = GEAR_ATTRS.filter((k) => mine[k] || theirs[k]).map((k) => ({ attr: k, mine: mine[k] ?? 0, worn: theirs[k] ?? 0 }));
  const pm = powers(item);
  const pw = worn ? powers(worn) : new Map<string, [PowerDiff['kind'], number]>();
  const diffs: PowerDiff[] = [];
  for (const [id, [kind, v]] of pm) if ((pw.get(id)?.[1] ?? 0) < v) diffs.push({ kind, key: id.slice(id.indexOf(':') + 1), gain: true });
  for (const [id, [kind, v]] of pw) if ((pm.get(id)?.[1] ?? 0) < v) diffs.push({ kind, key: id.slice(id.indexOf(':') + 1), gain: false });
  if (!worn) return { attrs, powers: diffs, verdict: 'empty' };
  const up = attrs.some((d) => d.mine > d.worn) || diffs.some((d) => d.gain);
  const down = attrs.some((d) => d.mine < d.worn) || diffs.some((d) => !d.gain);
  return { attrs, powers: diffs, verdict: up && down ? 'mixed' : up ? 'better' : down ? 'worse' : 'same' };
};
